/**
 * Feature planner: reserves ring intervals for set-piece runs that free
 * collapse can't guarantee — currently wide-channel runs (funnel in, wide
 * straights through, funnel out). Wide pieces bulge 0.35 into the flanking
 * cells, so both lateral neighbors of every cell in a run must be track-free;
 * that check happens here, at plan time, because reservations bypass the
 * WFC's candidate vetting. Width continuity is guaranteed by construction
 * (runs are always funnel-bracketed); realize() asserts it as an invariant.
 */
import type { Rng } from "./rng.ts";
import type { Skeleton } from "./topology.ts";
import type { Reservation } from "./elevation.ts";
import type { StyleField } from "./style.ts";
import { DIR_STEP, turnLeft, turnRight } from "./geom.ts";

/**
 * Runs BEFORE the elevation planner (the ring is all-flat at plan time);
 * elevation then avoids feature-reserved cells, which keeps every feature
 * window at constant base height by construction.
 */
export function planFeatures(
  rng: Rng,
  skeleton: Skeleton,
  style: StyleField,
): Reservation[] {
  const out: Reservation[] = [];
  const n = skeleton.cells.length;

  // Grade-crossing cells are spoken for (the cross piece owns them; a wide
  // channel through a crossing has no piece).
  const crossingIdx = new Set<number>();
  for (const { a, b } of skeleton.crossings) {
    crossingIdx.add(a);
    crossingIdx.add(b);
  }

  const wideRng = rng.child("wide");
  const want = wideRng.chance(style.wideAffinity)
    ? (wideRng.chance(style.wideAffinity * 0.5) ? 2 : 1)
    : 0;

  const ringCells = new Set(skeleton.cells.map((c) => `${c.x},${c.z}`));
  const claimedFlanks = new Set<string>();

  // Candidate intervals: maximal straight runs. The skeleton is normalized
  // to start at a turn, so runs never wrap the seam.
  const runs: { start: number; len: number }[] = [];
  let i = 0;
  while (i < n) {
    if (skeleton.cells[i].move !== "F" || crossingIdx.has(i)) {
      i++;
      continue;
    }
    let len = 0;
    while (
      i + len < n && skeleton.cells[i + len].move === "F" &&
      !crossingIdx.has(i + len)
    ) len++;
    if (len >= 3) runs.push({ start: i, len });
    i += len;
  }

  /** Both flank cells of every interval cell must be track-free and not
   * claimed by another feature. Returns the flank keys, or null. */
  const flanksClear = (start: number, total: number): string[] | null => {
    const flanks: string[] = [];
    for (let j = 0; j < total; j++) {
      const c = skeleton.cells[start + j];
      for (const side of [turnLeft(c.dirIn), turnRight(c.dirIn)]) {
        const [dx, dz] = DIR_STEP[side];
        const k = `${c.x + dx},${c.z + dz}`;
        if (ringCells.has(k) || claimedFlanks.has(k)) return null;
        flanks.push(k);
      }
    }
    return flanks;
  };

  let placed = 0;
  for (const run of wideRng.shuffle(runs)) {
    if (placed >= want) break;
    // funnel + 1..4 wide straights + funnel; long runs sometimes upgrade a
    // transition to funnel-long (straight + taper + wide fused, 3 cells).
    const entryLen = run.len >= 6 && wideRng.chance(0.35) ? 3 : 1;
    const exitLen = run.len >= entryLen + 5 && wideRng.chance(0.35) ? 3 : 1;
    const maxMid = Math.min(run.len - entryLen - exitLen, 4);
    if (maxMid < 1) continue;
    const mid = wideRng.int(1, maxMid + 1);
    const total = entryLen + mid + exitLen;
    const start = run.start + wideRng.int(0, run.len - total + 1);

    const flanks = flanksClear(start, total);
    if (!flanks) continue;

    out.push({
      start,
      len: entryLen,
      pieceIds: [entryLen === 3 ? "funnel-long" : "funnel"],
      reversed: false,
    });
    for (let j = 0; j < mid; j++) {
      out.push({
        start: start + entryLen + j,
        len: 1,
        pieceIds: ["straight-wide"],
        reversed: false,
      });
    }
    out.push({
      start: start + entryLen + mid,
      len: exitLen,
      pieceIds: [exitLen === 3 ? "funnel-long" : "funnel"],
      reversed: true,
    });
    for (const k of flanks) claimedFlanks.add(k);
    placed++;
  }

  // Split–merge diamond: one per track at most, on a straight run with both
  // flanks free for the whole span (the branches run one lane out each side).
  const diamondRng = rng.child("diamond");
  if (diamondRng.chance(0.4)) {
    const claimedIdx = new Set<number>();
    for (const r of out) {
      for (let j = 0; j < r.len; j++) claimedIdx.add(r.start + j);
    }
    const overlaps = (start: number, total: number): boolean => {
      for (let j = 0; j < total; j++) {
        if (claimedIdx.has(start + j)) return true;
      }
      return false;
    };
    // Exhaustive within each run (eligible geometry is scarce): prefer the
    // longest branch that fits, sliding the window across the run.
    placing:
    for (const run of diamondRng.shuffle(runs)) {
      const maxN = Math.min(3, run.len - 4);
      for (let N = maxN; N >= 1; N--) {
        const total = 4 + N;
        const offsets = diamondRng.shuffle(
          Array.from({ length: run.len - total + 1 }, (_, o) => o),
        );
        for (const o of offsets) {
          const start = run.start + o;
          if (overlaps(start, total)) continue;
          const flanks = flanksClear(start, total);
          if (!flanks) continue;
          out.push({
            start,
            len: total,
            pieceIds: [`diamond-${N}`],
            reversed: false,
          });
          for (const k of flanks) claimedFlanks.add(k);
          break placing;
        }
      }
    }
  }
  // DOUBLE DIAMOND (M19c): a SECOND split–merge event on another run —
  // two lane choices per lap. (A back-to-back chicane pair was ruled out
  // by measurement: mutated rings top out at ~9-cell straight runs and
  // the pair needs 11.) Own child stream — label-derived, so nothing
  // above moves; eligible leftover geometry is scarce, which keeps a
  // twin-diamond lap an event.
  const ddRng = rng.child("diamond2");
  if (ddRng.chance(0.5)) {
    const claimedIdx = new Set<number>();
    for (const r of out) {
      for (let j = 0; j < r.len; j++) claimedIdx.add(r.start + j);
    }
    const overlaps = (start: number, total: number): boolean => {
      for (let j = 0; j < total; j++) {
        if (claimedIdx.has(start + j)) return true;
      }
      return false;
    };
    placing:
    for (const run of ddRng.shuffle(runs)) {
      const maxN = Math.min(3, run.len - 4);
      for (let N = maxN; N >= 1; N--) {
        const total = 4 + N;
        const offsets = ddRng.shuffle(
          Array.from({ length: run.len - total + 1 }, (_, o) => o),
        );
        for (const o of offsets) {
          const start = run.start + o;
          if (overlaps(start, total)) continue;
          const flanks = flanksClear(start, total);
          if (!flanks) continue;
          out.push({
            start,
            len: total,
            pieceIds: [`diamond-${N}`],
            reversed: false,
          });
          for (const k of flanks) claimedFlanks.add(k);
          break placing;
        }
      }
    }
  }
  return out;
}
